import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { truncate } from "lodash";

import CallPatientLink from "components/suno-dialer/call-patient/call-patient-link.component";
import { COMMUNICATION_METHOD_TYPES } from "constants/patients";
import useMostRecentIcd10Codes from "features/patients/hooks/use-most-recent-icd10-codes.hook";
import HeaderLastReportDate from "features/patients/patient-header/expanded-header/header-last-report-date.component";
import HeaderMostRecentDiagnosis from "features/patients/patient-header/expanded-header/header-most-recent-diagnosis.component";
import usePatientHeaderReports from "features/patients/patient-header/use-patient-header-reports.hook";
import { PureToneAudioGramProvider } from "features/patients/sections/patient-audiology/test-results/pure-tone-audiogram/pure-tone-audiogram.hook";
import { ICD10Code } from "services/icd10-codes";
import type { Patient } from "services/types/patients.type";
import type { Physician } from "services/types/physician.type";
import {
  formatBackendDateOnly,
  formatPhoneNumber,
  getFullName
} from "utils/common";
import { getFullAddress } from "utils/patients.util";

function InfoRow({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack direction="row" gap={1.5}>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 72, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{ wordBreak: "break-word" }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

function PhysicianBlock({
  label,
  physician
}: {
  label: string;
  physician: Physician;
}) {
  const name = getFullName({
    firstName: physician.first_name ?? "",
    lastName: physician.last_name ?? "",
    title: physician.title,
    suffix: physician.suffix
  });

  return (
    <Stack gap={0.75}>
      <Stack direction="row" gap={1.5}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 72, flexShrink: 0 }}
        >
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.25}>
          {physician.id ? (
            <Link
              href={`/settings/physicians/${physician.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              variant="body2"
            >
              {name}
            </Link>
          ) : (
            <Typography variant="body2">{name}</Typography>
          )}
          {/* {physician.id && (
            <Tooltip title="Open physician profile" placement="top">
              <IconButton
                size="small"
                href={`/settings/physicians/${physician.id}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                sx={{ p: 0.25 }}
              >
                <OpenInNewIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          )} */}
        </Stack>
      </Stack>
      {physician.phone && (
        <InfoRow label="Phone">{formatPhoneNumber(physician.phone)}</InfoRow>
      )}
      {physician.email && (
        <InfoRow label="Email">
          <Tooltip
            title={physician.email.length > 25 ? physician.email : ""}
            placement="top-start"
          >
            <Link
              href={`mailto:${physician.email}`}
              underline="hover"
              variant="body2"
            >
              {truncate(physician.email, { length: 25 })}
            </Link>
          </Tooltip>
        </InfoRow>
      )}
      {physician.fax_phone && (
        <InfoRow label="Fax">{formatPhoneNumber(physician.fax_phone)}</InfoRow>
      )}
    </Stack>
  );
}

interface PatientContactInformationProps {
  patient: Patient;
}

export const PatientContactInformation: React.FC<
  PatientContactInformationProps
> = ({ patient }) => {
  const { mostRecentIcd10Codes } = useMostRecentIcd10Codes(patient.id);
  const { latestAudiogram, isLatestAudiogramLoading } = usePatientHeaderReports(
    patient.id
  );

  const preferredMethod = COMMUNICATION_METHOD_TYPES.getFromCode(
    patient.preferred_communication_method
  );
  const fullAddress = getFullAddress(patient, true);
  const hasClinicSnapshot =
    mostRecentIcd10Codes.length > 0 ||
    Boolean(patient.last_audiogram_date) ||
    isLatestAudiogramLoading;

  return (
    <Stack gap={2}>
      {/* CONTACT */}
      <Stack gap={0.75}>
        <Typography variant="body3" fontWeight="700">
          Contact
        </Typography>
        <CallPatientLink
          label={
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 72, flexShrink: 0 }}
            >
              Phone
            </Typography>
          }
          variant="body2"
          patient={patient}
          containerSx={{ mb: -1 }}
        >
          <Stack direction="row" gap={1.5} alignItems="center">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 72, flexShrink: 0 }}
            >
              Phone
            </Typography>
            <Typography variant="body2">
              {formatPhoneNumber(patient.phone)}
            </Typography>
          </Stack>
        </CallPatientLink>
        {patient.email && (
          <InfoRow label="Email">
            <Tooltip
              title={patient.email.length > 25 ? patient.email : ""}
              placement="top-start"
            >
              <Link
                href={`mailto:${patient.email}`}
                underline="hover"
                variant="body2"
              >
                {truncate(patient.email, { length: 25 })}
              </Link>
            </Tooltip>
          </InfoRow>
        )}
        {preferredMethod && (
          <InfoRow label="Preferred">{preferredMethod}</InfoRow>
        )}
        {fullAddress && <InfoRow label="Address">{fullAddress}</InfoRow>}
      </Stack>

      <Divider />

      {/* PHYSICIANS */}
      {(patient.primary_care_physician || patient.referring_physician) && (
        <>
          <Stack gap={0.75}>
            <Typography variant="body3" fontWeight="700">
              Physicians
            </Typography>
            {patient.primary_care_physician && (
              <PhysicianBlock
                label="Primary"
                physician={patient.primary_care_physician}
              />
            )}
            {patient.primary_care_physician && patient.referring_physician && (
              <Divider sx={{ my: 0.5 }} />
            )}
            {patient.referring_physician && (
              <PhysicianBlock
                label="Referring"
                physician={patient.referring_physician}
              />
            )}
          </Stack>
          <Divider />
        </>
      )}

      {/* PRACTICE DETAILS */}
      <Stack gap={0.75}>
        <Typography variant="body3" fontWeight="700">
          Practice Details
        </Typography>
        {patient.preferred_provider?.fullName && (
          <InfoRow label="Provider">
            {patient.preferred_provider.fullName}
          </InfoRow>
        )}
        {patient.preferred_clinic?.name && (
          <InfoRow label="Clinic">{patient.preferred_clinic.name}</InfoRow>
        )}
        {patient.referral_source?.name && (
          <InfoRow label="Referral">{patient.referral_source.name}</InfoRow>
        )}
        {patient.created_at_display && (
          <InfoRow label="Added">{patient.created_at_display}</InfoRow>
        )}
        {patient.hipaa_sign_date ? (
          <InfoRow label="HIPAA">
            Signed{" "}
            {formatBackendDateOnly(patient.hipaa_sign_date, "MM/dd/yyyy")}
          </InfoRow>
        ) : (
          <InfoRow label="HIPAA">Not Signed</InfoRow>
        )}
      </Stack>

      {hasClinicSnapshot && (
        <>
          <Divider />

          {/* CLINICAL SNAPSHOT */}
          <Stack gap={0.75}>
            <Typography variant="body3" fontWeight="700">
              Clinical Snapshot
            </Typography>
            {mostRecentIcd10Codes.length > 0 && (
              <InfoRow label="Most Recent Diagnosis (ICD-10)">
                <HeaderMostRecentDiagnosis
                  mostRecentIcd10Codes={mostRecentIcd10Codes as ICD10Code[]}
                  showLabel={false}
                  sx={{ background: "none", px: 0, py: 0, width: "auto" }}
                  typographySx={{ fontSize: "14px" }}
                />
              </InfoRow>
            )}
            <PureToneAudioGramProvider
              currentReport={latestAudiogram}
              isLoadingReport={isLatestAudiogramLoading}
            >
              <HeaderLastReportDate patientId={patient.id} variant="info-row" />
            </PureToneAudioGramProvider>
          </Stack>
        </>
      )}
    </Stack>
  );
};
